import { Suspense, useMemo, useState } from "react";
import { Await, useLocation, useSearchParams } from "react-router";

import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { CaretDownIcon, RulerHorizontalIcon } from "@radix-ui/react-icons";

import { GlobalErrors } from "~/components/forms/GlobalErrors";
import { HorizontalSlider } from "~/components/horizontal-slider";
import ProductQuantitySelector from "~/components/products/product-quantity-selector";
import VariantSelector from "~/components/products/product-variant-selector";
import { Alert, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { SwatchCrossout } from "~/components/ui/icons/swatch-crossout";
import { Label } from "~/components/ui/label";
import { TriButton } from "~/components/ui/tri-button";
import * as gtag from "~/google-analytics/utils/gtags.client";
import useImageUtils from "~/hooks/useImageUtils";
import type { Product } from "~/lib/app.types";
import { LOW_STOCK_NUM } from "~/lib/constants";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { useFormatPrice } from "~/shop/utils/price";
import { getSelectedVariant } from "~/shop/utils/variants";

import { useBasketActions } from "../../resources/basket";
import { WishlistCTA } from "../../resources/wishlist";

const SwatchOptions = ({ selected }: { selected: string }) => {
  const { getImageLink } = useImageUtils();
  return (
    <VariantSelector.OptionValues>
      {({ option, setSelected, swatch, variationGroup }) => (
        <span
          aria-hidden="true"
          key={`${option.name}-${option.value}`}
          onClick={() => {
            setSelected(option.value);
          }}
          className={cn(
            option.name,
            "relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full p-1",
            selected === swatch?.color.value && "border border-accent-11",
            variationGroup?.orderable === true ? "" : "opacity-50",
          )}
        >
          {swatch && swatch?.images?.swatch?.images?.[0]?.link && (
            <img
              src={getImageLink(swatch.images.swatch.images?.[0]?.link)}
              alt={swatch?.name}
              className="h-full w-full rounded-full object-cover"
            />
          )}
          {!variationGroup?.orderable && (
            <SwatchCrossout className="absolute h-10 w-10" />
          )}
        </span>
      )}
    </VariantSelector.OptionValues>
  );
};

const LegLengthOptions = ({ selected }: { selected: string }) => (
  <VariantSelector.OptionValues>
    {({ option, setSelected, variationGroup }) => (
      <Button
        variant="outline"
        key={`${option.name}-${option.value}`}
        className={cn(
          "inline-block h-7 border border-neutral-3 bg-neutral-1 text-neutral-11 shadow-sm [&:hover]:border-accent-11 [&:hover]:bg-panel-translucent",
          selected === option.value &&
            "border border-accent-11 bg-accentalpha-10 text-text-light-on-dark [&:hover]:border-accent-11 [&:hover]:bg-accentalpha-10 [&:hover]:text-text-light-on-dark",
          variationGroup?.orderable === true ? "" : "opacity-50",
        )}
        onClick={() => setSelected(option.value)}
      >
        {option.name}
      </Button>
    )}
  </VariantSelector.OptionValues>
);

const SizeOptions = ({
  selected,
  allSelected,
  master,
}: {
  selected: string;
  allSelected: {
    [key: string]: string;
  };
  master:
    | Promise<SerializeFrom<Product> | null>
    | SerializeFrom<Product>
    | null;
}) => (
  <VariantSelector.OptionValues>
    {({ option, setSelected, selectedValue }) => {
      return (
        <Suspense
          key={`${option.name}-${option.value}`}
          fallback={
            <Button
              variant="ghost"
              className={cn(
                "inline-block h-7 border border-neutral-3 bg-neutral-1 text-neutral-11 shadow-sm [&:hover]:border-accent-11 [&:hover]:bg-panel-translucent",
                selected === option.value &&
                  "border border-accent-11 bg-accentalpha-10 text-text-light-on-dark [&:hover]:border-accent-11 [&:hover]:bg-accentalpha-10 [&:hover]:text-text-light-on-dark",
              )}
              onClick={() => setSelected(option.value)}
            >
              {option.name}
            </Button>
          }
        >
          <Await resolve={master}>
            {master => {
              const variant = getSelectedVariant(master, {
                ...allSelected,
                size: option.value,
              });

              return (
                <Button
                  variant="ghost"
                  key={`${option.name}-${option.value}`}
                  className={cn(
                    "inline-block h-7 border border-neutral-3 bg-neutral-1 text-neutral-11 shadow-sm [&:hover]:border-accent-11 [&:hover]:bg-panel-translucent",
                    selected === option.value &&
                      "border border-accent-11 bg-accentalpha-10 text-text-light-on-dark [&:hover]:border-accent-11 [&:hover]:bg-accentalpha-10 [&:hover]:text-text-light-on-dark",
                    !variant?.orderable && "opacity-50",
                  )}
                  onClick={() => setSelected(option.value)}
                >
                  {option.name}
                </Button>
              );
            }}
          </Await>
        </Suspense>
      );
    }}
  </VariantSelector.OptionValues>
);

export const ProductBuyForm = ({
  product,
  productId,
  master,
  openSizeGuide,
  wrapOptions,
  children,
}: {
  productId: string;
  product: SerializeFrom<Product>;
  master:
    | Promise<SerializeFrom<Product> | null>
    | SerializeFrom<Product>
    | null;
  openSizeGuide: () => void;
  wrapOptions?: boolean;
  children?: React.ReactNode;
}) => {
  const state = useLocation().state;
  const [variantError, setVariantError] = useState<boolean>(false);
  const [productQuantity, setProductQuantity] = useState<string>("1");
  const basket = useBasketActions();
  const [params, setSearchParams] = useSearchParams();
  const objectParams = useMemo(
    () =>
      ({
        ...product?.variationValues,
        ...Object.fromEntries(params),
      }) as {
        [key: string]: string;
      },
    [product?.variationValues],
  );
  const isLoading = basket.fetcher.state !== "idle";
  const formatPrice = useFormatPrice();
  const defaultSize =
    product.variationAttributes?.find(x => x.id === "size")?.values?.[0]
      ?.value || "";

  if (!Object.fromEntries(params).size) {
    objectParams.size = defaultSize;
  }

  const addGtagEvent = () => {
    /* For GA4 */
    gtag.event({
      action: "add_to_bag_clicked",
      category: "product_buy_form",
      label: "added_product_id",
      value: productId,
    });
  };

  return (
    <VariantSelector.Selector
      product={product}
      master={master}
      productId={productId}
      defaultValue={objectParams}
      onChange={values => {
        setSearchParams(
          prev => {
            const params = new URLSearchParams(prev);

            Object.entries(values).forEach(([key, value]) => {
              if (value) {
                params.set(key, value);
              } else {
                params.delete(key);
              }
            });

            return params;
          },
          {
            replace: true,
            preventScrollReset: true,
            state,
          },
        );
      }}
    >
      {children}
      <div>
        <div className="flex flex-col items-stretch gap-4">
          <VariantSelector.OptionSelect type="color">
            {({ selected, attribute }) => (
              <>
                <div className="relative flex flex-col items-start gap-1.5 self-stretch py-0">
                  <Label className="text-sm font-semibold">
                    {attribute.name || attribute.id}:{" "}
                    <span className="font-light">
                      {attribute.values?.find(v => v.value === selected)?.name}
                    </span>
                  </Label>
                  {wrapOptions ? (
                    <div className="flex flex-row flex-wrap gap-2">
                      <SwatchOptions selected={selected} />
                    </div>
                  ) : (
                    <HorizontalSlider controlsEnabled>
                      <SwatchOptions selected={selected} />
                    </HorizontalSlider>
                  )}
                </div>
              </>
            )}
          </VariantSelector.OptionSelect>
          <VariantSelector.OptionSelect type="leg-length">
            {({ selected, attribute }) => (
              <div className="relative flex w-full flex-col items-start gap-1.5 self-stretch py-0">
                <Label className="text-sm font-semibold">
                  {attribute.name || attribute.id}:{" "}
                  <span className="font-light">
                    {attribute.values?.find(v => v.value === selected)?.name}
                  </span>
                </Label>
                {wrapOptions ? (
                  <div className="flex flex-row flex-wrap gap-2">
                    <LegLengthOptions selected={selected} />
                  </div>
                ) : (
                  <HorizontalSlider controlsEnabled>
                    <LegLengthOptions selected={selected} />
                  </HorizontalSlider>
                )}
              </div>
            )}
          </VariantSelector.OptionSelect>
          <VariantSelector.OptionSelect type="size">
            {({ selected, attribute, allSelected }) => (
              <div className="relative flex w-full flex-col items-start gap-1.5 self-stretch py-0">
                <Label className="w-full text-sm font-semibold">
                  {attribute.name || attribute.id}:{" "}
                  <span className="font-light">
                    {attribute.values?.find(v => v.value === selected)?.name}
                  </span>
                  {Boolean(
                    product?.inventory?.stockLevel &&
                      product?.inventory?.stockLevel < LOW_STOCK_NUM,
                  ) && (
                    <span className="rounded-[3px] border border-accentalpha-7 bg-surface-accent-light px-2 text-accentalpha-11">
                      low stock
                    </span>
                  )}
                  <button
                    onClick={openSizeGuide}
                    tabIndex={0}
                    role="link"
                    className="float-right cursor-pointer text-accentalpha-11"
                  >
                    <RulerHorizontalIcon className="mr-2 inline h-4 text-text-dark-on-light" />
                    Size Guide
                  </button>
                </Label>
                {wrapOptions ? (
                  <div className="flex flex-row flex-wrap gap-2">
                    <SizeOptions
                      allSelected={allSelected}
                      selected={selected}
                      master={master}
                    />
                  </div>
                ) : (
                  <HorizontalSlider controlsEnabled>
                    <SizeOptions
                      allSelected={allSelected}
                      selected={selected}
                      master={master}
                    />
                  </HorizontalSlider>
                )}
              </div>
            )}
          </VariantSelector.OptionSelect>
        </div>

        {/* Display server-side  errors */}
        <GlobalErrors data={basket.fetcher.data} />

        {variantError && (
          <Alert className="mt-3" variant="error">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Select all product attributes</AlertTitle>
          </Alert>
        )}

        <div className="mt-5 flex w-full flex-col sm:flex-col">
          <Suspense
            fallback={
              <div className="flex w-full flex-1-0-0 flex-row gap-4 self-stretch">
                <div className="flex h-7 w-[50px] flex-row items-center justify-center rounded-md border border-neutralalpha-6 bg-neutralalpha-4 px-3 py-2 text-neutralalpha-8">
                  1<CaretDownIcon height={24} />
                </div>
                <TriButton
                  type="submit"
                  disabled={true}
                  className=""
                  name="action"
                  value="add"
                >
                  Add to bag
                </TriButton>
                <WishlistCTA
                  productId=""
                  className=""
                  iconHeight={20}
                  iconWidth={20}
                  isPDP={true}
                />
              </div>
            }
          >
            <Await resolve={master}>
              {master => {
                return (
                  <VariantSelector.SelectedVariant>
                    {({
                      salePrice,
                      currentPrice,
                      variant,
                      selected,
                      allAttributesSelected,
                    }) => {
                      const isOutOfStock =
                        variant?.orderable === false ||
                        (!variant && allAttributesSelected);
                      return (
                        <>
                          <div className="flex w-full flex-1-0-0 flex-row gap-4 self-stretch">
                            <div className="flex">
                              <ProductQuantitySelector
                                productQuantity={productQuantity}
                                setProductQuantity={setProductQuantity}
                              />
                            </div>
                            <div className="flex w-[-webkit-fill-available]">
                              <TriButton
                                type="submit"
                                disabled={isLoading || isOutOfStock}
                                className="w-[inherit] animate-popupStrong"
                                isLoading={isLoading}
                                name="action"
                                value="add"
                                data-testid="product-add-to-bag-button"
                                onClick={() => {
                                  /* We want to keep the button available but show error if not all attributes are selected */
                                  if (!variant) {
                                    setVariantError(true);
                                    return;
                                  }
                                  /* assume all attributes selected from here */
                                  basket.add(
                                    {
                                      productId: variant.productId,
                                      ...selected,
                                    },
                                    parseInt(productQuantity),
                                  );
                                  setVariantError(false);
                                  addGtagEvent();
                                }}
                              >
                                {isOutOfStock ? "Out of stock" : "Add to bag"}
                              </TriButton>
                              <WishlistCTA
                                productId={variant?.productId ?? ""}
                                className="m-auto px-4"
                                iconHeight={20}
                                iconWidth={20}
                                isPDP={true}
                              />
                            </div>
                          </div>
                        </>
                      );
                    }}
                  </VariantSelector.SelectedVariant>
                );
              }}
            </Await>
          </Suspense>
        </div>
      </div>
    </VariantSelector.Selector>
  );
};
